import axios from "axios";
import cogoToast from "cogo-toast";
import config from "../../utils/config";

const { createSlice, createAsyncThunk, isAnyOf } = require("@reduxjs/toolkit");

// get all clients
export const getClientProducts = createAsyncThunk(
  "auth/getClientProducts",
  async (
    {
      seasonalCategoryId,
      setProducts,
      setTotalRecords,
      setResponseInfo,
      setSeasonLine,
      viewIndex = 0,
      viewSize = 10,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();

      let params = {
        viewIndex,
        viewSize,
        webSiteId: config.websiteId,
      };

      if (auth?.selectedCategories?.length > 0) {
        params = { ...params, productCategoryIds: auth?.selectedCategories };
      } else {
        params = { ...params, seasonalCategoryId };
      }

      const response = await axios.post("/services/getClientProducts", params);

      if (response.status === 200) {
        const { productAndCategoryMembers, totalRecords, responseMap, seasonLine } =
          response?.data?.data;
        setProducts(productAndCategoryMembers || []);
        setTotalRecords(totalRecords || 0);
        if (setResponseInfo) {
          setResponseInfo(responseMap || null);
        }
        if (setSeasonLine) {
          setSeasonLine(seasonLine || null);
        }
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get all product categories
export const getClientProductCategory = createAsyncThunk(
  "auth/getClientProductCategory",
  async ({ setCategories }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const agreementId = auth?.client?.agreementId || "";
      const partyId = auth?.client?.partyId || "";
      const catalogType = auth?.selectedCatalogType || "";
      const response = await axios.get(
        `/services/getClientProductCategory?inParams=${encodeURIComponent(
          JSON.stringify({ agreementId, partyId, catalogType })
        )}`
      );

      if (response.status === 200) {
        setCategories(response?.data?.data?.productCategoryIds || []);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get all product categories
export const getClientProductMatrix = createAsyncThunk(
  "auth/getClientProductMatrix",
  async ({ data, setProductMatrixData, setOrderId, setSubstituteRef }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/services/getClientProductMatrix",
        data
      );

      if (response.status === 200) {
        setProductMatrixData(response?.data?.data?.productMatrixList);
        if (setOrderId) {
          setOrderId(response?.data?.data?.orderId || null);
        }
        // Substitute carts only: the return and the original order it replaces
        if (setSubstituteRef) {
          const { returnId, originalOrderId } = response?.data?.data || {};
          setSubstituteRef(returnId ? { returnId, originalOrderId } : null);
        }
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get product content
export const getProductContent = createAsyncThunk(
  "auth/getProductContent",
  async ({ productId, setProductContent }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/getProductContent", {
        productId,
      });

      if (response.status === 200) {
        setProductContent(response?.data?.data?.productContent);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Add or update item to cart
export const addOrUpdateItemToCart = createAsyncThunk(
  "auth/addOrUpdateItemToCart",
  async ({ productList, callback }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const agreementId = auth.client?.agreementId || "";

      console.log("addOrUpdateItemToCart - agreementId:", agreementId);

      const response = await axios.post("/services/addOrUpdateItemToCart", {
        productList,
      });

      if (response.status === 200) {
        cogoToast.success("Cart updated successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback();
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Remove a single line (by productId) from the cart
export const removeItemFromCart = createAsyncThunk(
  "auth/removeItemFromCart",
  async ({ productId, callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/removeItemFromCart", {
        productId,
      });

      if (response.status === 200) {
        cogoToast.success("Item removed from cart!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback();
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Create client order
export const createClientOrder = createAsyncThunk(
  "auth/createClientOrder",
  async ({ callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/createClientOrder", {});

      const payload = response?.data?.data ?? {};

      // A failure (e.g. substitute order below its minimum value) comes back as
      // HTTP 200 with an `emessage` field. The cart is kept server-side, so the
      // callback (which drops the cart token and navigates away) must not run.
      if (payload?.emessage) {
        cogoToast.error(payload.emessage, {
          position: "top-right",
          hideAfter: 5,
        });
        return rejectWithValue(payload.emessage);
      }

      if (response.status === 200) {
        cogoToast.success("Order created successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback(payload.orderId);
      }
      return payload;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Clear cart
export const clearCart = createAsyncThunk(
  "auth/clearCart",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/clearCart", {});

      if (response.status === 200) {
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Read-only summary of the order being built in the cart: kind (NEW / EDIT / SUBSTITUTION), ids, agreement category
export const getCartInfo = createAsyncThunk(
  "auth/getCartInfo",
  async ({ setCartInfo }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/getCartInfo", {});
      if (response.status === 200 && setCartInfo) {
        setCartInfo(response?.data?.data || null);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Get Categories
export const getProductCategoryRollups = createAsyncThunk(
  "auth/getProductCategoryRollups",
  async (
    { setCategoryRollups, parentProductCategoryId },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `/services/getProductCategoryRollups?inParams=${encodeURIComponent(
          JSON.stringify({ parentProductCategoryId })
        )}`
      );

      if (response.status === 200) {
        setCategoryRollups(response?.data?.data?.categories || []);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  products: [],
  isLoading: false,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setProducts(state, action) {
      state.products = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          getClientProducts.pending,
          getProductContent.pending,
          addOrUpdateItemToCart.pending,
          removeItemFromCart.pending,
          createClientOrder.pending,
          getClientProductMatrix.pending
        ),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          getClientProducts.fulfilled,
          getClientProducts.rejected,
          getProductContent.fulfilled,
          getProductContent.rejected,
          addOrUpdateItemToCart.fulfilled,
          addOrUpdateItemToCart.rejected,
          removeItemFromCart.fulfilled,
          removeItemFromCart.rejected,
          createClientOrder.fulfilled,
          createClientOrder.rejected,
          getClientProductMatrix.fulfilled,
          getClientProductMatrix.rejected
        ),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const productSelector = (state) => state.product;

export const { setProducts } = productSlice.actions;
export default productSlice.reducer;
